import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listAiKeywords,
  addAiKeywords,
  deleteAiKeyword,
  checkAiKeyword,
  aiCheckAll,
  aiVisibility,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  CheckCircle,
  Copy,
  XCircle,
  Plus,
  Trash,
  Sparkle,
  Robot,
} from "@phosphor-icons/react";
import { SiOpenai, SiAnthropic, SiGooglegemini } from "react-icons/si";

const MAX_AI_KEYWORDS = 20;

const AI_META = {
  chatgpt: { label: "ChatGPT", color: "#10A37F", Icon: SiOpenai },
  claude: { label: "Claude", color: "#D97706", Icon: SiAnthropic },
  gemini: { label: "Gemini", color: "#4285F4", Icon: SiGooglegemini },
};

function VisibilityCard({ modelKey, summary }) {
  const meta = AI_META[modelKey];
  const pct = summary?.visibility_percent ?? 0;
  const mentioned = summary?.mentioned_count ?? 0;
  const total = summary?.total_checked ?? 0;
  const avg = summary?.avg_position;
  const Icon = meta.Icon;
  return (
    <div
      className="border border-[var(--rt-border)] bg-white p-5"
      data-testid={`ai-vis-card-${modelKey}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon size={18} color={meta.color} />
          <span className="font-display text-lg leading-none">{meta.label}</span>
        </div>
        <span
          className="px-2 py-0.5 text-[10px] font-mono-data font-semibold"
          style={{ background: meta.color + "15", color: meta.color }}
        >
          AI VISIBILITY
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-5xl leading-none">{pct}</span>
        <span className="font-display text-xl text-[var(--rt-text-muted)]">%</span>
      </div>
      <div className="mt-2 h-1.5 bg-[#F1F1F2] overflow-hidden">
        <div
          className="h-full transition-all duration-700"
          style={{ width: `${pct}%`, background: meta.color }}
        />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 pt-3 border-t border-[var(--rt-border)]">
        <div>
          <div className="label-eyebrow text-[10px]">MENTIONED IN</div>
          <div className="font-mono-data text-sm mt-0.5">
            {mentioned}/{total}
          </div>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">AVG RANK</div>
          <div className="font-mono-data text-sm mt-0.5">{avg ?? "—"}</div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ result, modelKey }) {
  const meta = AI_META[modelKey];
  if (!result || !result.checked_at) {
    return (
      <span className="text-xs font-mono-data text-[var(--rt-text-muted)]">—</span>
    );
  }
  if (!result.ok) {
    return (
      <span className="text-xs font-mono-data text-[var(--rt-text-muted)]" title={result.error}>
        ERR
      </span>
    );
  }
  if (result.mentioned) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-mono-data font-semibold" style={{ color: meta.color }}>
        <CheckCircle size={13} weight="fill" />#{result.position}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-mono-data text-[var(--rt-text-muted)]">
      <XCircle size={13} /> no
    </span>
  );
}

function AddAiKeywordsDialog({ websiteId, remaining }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const qc = useQueryClient();
  const parsed = raw.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const mut = useMutation({
    mutationFn: () => addAiKeywords(websiteId, parsed),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ai-keywords", websiteId] });
      qc.invalidateQueries({ queryKey: ["ai-visibility", websiteId] });
      toast.success(`${data.inserted} AI keyword(s) added`);
      setOpen(false);
      setRaw("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-add-ai-keywords"
          disabled={remaining <= 0}
          className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white px-4 h-10 flex items-center gap-2"
        >
          <Plus size={16} weight="bold" />
          Add AI keywords
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-[var(--rt-border)] bg-white max-w-xl">
        <DialogHeader>
          <div className="label-eyebrow mb-2">/ priority queries</div>
          <DialogTitle className="font-display text-2xl">Add AI-tracked keywords</DialogTitle>
          <DialogDescription className="text-sm text-[var(--rt-text-muted)]">
            Up to {MAX_AI_KEYWORDS} priority keywords per website. Each AI charge runs ~$0.001/keyword.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="ai-keywords-textarea"
          rows={8}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          className="rounded-none border-[var(--rt-border)] font-mono-data text-sm"
          placeholder={"best espresso machine\nseo agency dallas\ncold brew recipe"}
        />
        <div className="text-xs text-[var(--rt-text-muted)]">
          <span className="font-mono-data text-[var(--rt-text)]">{parsed.length}</span> parsed ·{" "}
          <span className="font-mono-data text-[var(--rt-text)]">{remaining}</span> slots remaining
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="submit-add-ai-keywords"
            disabled={parsed.length === 0 || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white"
          >
            {mut.isPending ? "Adding…" : `Add ${Math.min(parsed.length, remaining)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AiVisibilityTab({ websiteId }) {
  const qc = useQueryClient();

  const { data: keywords = [], isLoading } = useQuery({
    queryKey: ["ai-keywords", websiteId],
    queryFn: () => listAiKeywords(websiteId),
  });
  const { data: summary } = useQuery({
    queryKey: ["ai-visibility", websiteId],
    queryFn: () => aiVisibility(websiteId),
  });

  const checkAllMut = useMutation({
    mutationFn: () => aiCheckAll(websiteId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ai-keywords", websiteId] });
      qc.invalidateQueries({ queryKey: ["ai-visibility", websiteId] });
      toast.success(`Checked ${data.checked}/${data.total} keywords across 3 AIs`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Check failed"),
  });

  const checkOneMut = useMutation({
    mutationFn: (id) => checkAiKeyword(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-keywords", websiteId] });
      qc.invalidateQueries({ queryKey: ["ai-visibility", websiteId] });
      toast.success("Updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const delMut = useMutation({
    mutationFn: (id) => deleteAiKeyword(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-keywords", websiteId] });
      qc.invalidateQueries({ queryKey: ["ai-visibility", websiteId] });
    },
  });

  const remaining = MAX_AI_KEYWORDS - keywords.length;
  const hasAny = keywords.length > 0;

  return (
    <div data-testid="ai-visibility-tab">
      {/* Top bar */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={14} weight="fill" style={{ color: "#F4B400" }} />
            <span className="label-eyebrow">/ ai visibility</span>
          </div>
          <h2 className="font-display text-3xl leading-tight">
            Are you cited by the AIs?
          </h2>
          <p className="text-sm text-[var(--rt-text-muted)] mt-2 max-w-xl leading-relaxed">
            We ask ChatGPT, Claude, and Gemini who they&apos;d cite as authoritative for each keyword,
            then check whether your domain appears in their answers. Higher % = higher chance your
            site gets cited in AI search.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="ai-copy-list-btn"
            onClick={async () => {
              if (keywords.length === 0) return;
              try {
                await navigator.clipboard.writeText(keywords.map((k) => k.keyword).join("\n"));
                toast.success(`Copied ${keywords.length} keyword(s) to clipboard`);
              } catch {
                toast.error("Could not copy");
              }
            }}
            disabled={keywords.length === 0}
            className={`rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 inline-flex items-center gap-2 text-sm font-medium ${keywords.length === 0 ? "opacity-50 pointer-events-none" : ""}`}
            title="Copy all keywords to clipboard"
          >
            <Copy size={14} weight="bold" />
            Copy list
          </button>
          <Button
            data-testid="ai-check-all-btn"
            onClick={() => checkAllMut.mutate()}
            disabled={!hasAny || checkAllMut.isPending}
            className="rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 flex items-center gap-2"
          >
            <ArrowsClockwise size={14} className={checkAllMut.isPending ? "animate-spin" : ""} />
            {checkAllMut.isPending ? "Checking…" : "Check all (3 AIs)"}
          </Button>
          <AddAiKeywordsDialog websiteId={websiteId} remaining={remaining} />
        </div>
      </div>

      {/* Visibility cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {["chatgpt", "claude", "gemini"].map((k) => (
          <VisibilityCard key={k} modelKey={k} summary={summary?.per_model?.[k]} />
        ))}
      </div>

      {/* Keywords table */}
      <div className="border border-[var(--rt-border)] bg-white">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 label-eyebrow border-b border-[var(--rt-border)] bg-[#FAFAFA] items-center">
          <div className="col-span-5">Keyword</div>
          <div className="col-span-2 text-right">ChatGPT</div>
          <div className="col-span-2 text-right">Claude</div>
          <div className="col-span-2 text-right">Gemini</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--rt-text-muted)]">Loading…</div>
        ) : keywords.length === 0 ? (
          <div className="px-6 py-14 text-center" data-testid="ai-empty-state">
            <Robot size={40} weight="duotone" className="mx-auto mb-4 text-[var(--rt-text-muted)]" />
            <div className="font-display text-xl mb-2">No AI keywords yet</div>
            <p className="text-sm text-[var(--rt-text-muted)] mb-5 max-w-md mx-auto">
              Add up to {MAX_AI_KEYWORDS} priority keywords. We&apos;ll ask 3 AIs and report whether your domain is cited.
            </p>
            <AddAiKeywordsDialog websiteId={websiteId} remaining={MAX_AI_KEYWORDS} />
          </div>
        ) : (
          keywords.map((k) => {
            const r = k.results || {};
            return (
              <div
                key={k.id}
                data-testid={`ai-kw-row-${k.id}`}
                className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[var(--rt-border)] last:border-b-0 rt-row-zebra hover:bg-[var(--rt-surface-hover)] items-center"
              >
                <div className="col-span-5">
                  <div className="text-sm truncate">{k.keyword}</div>
                  <div className="text-[10px] text-[var(--rt-text-muted)] mt-0.5 font-mono-data">
                    {k.last_checked_at ? new Date(k.last_checked_at).toLocaleString() : "Never checked"}
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <StatusPill modelKey="chatgpt" result={r.chatgpt} />
                </div>
                <div className="col-span-2 text-right">
                  <StatusPill modelKey="claude" result={r.claude} />
                </div>
                <div className="col-span-2 text-right">
                  <StatusPill modelKey="gemini" result={r.gemini} />
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button
                    onClick={() => checkOneMut.mutate(k.id)}
                    disabled={checkOneMut.isPending}
                    data-testid={`ai-check-${k.id}`}
                    className="p-1.5 hover:bg-[var(--rt-surface-hover)] text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] transition-colors"
                    title="Re-check"
                  >
                    <ArrowsClockwise size={14} />
                  </button>
                  <button
                    onClick={() => delMut.mutate(k.id)}
                    data-testid={`ai-del-${k.id}`}
                    className="p-1.5 hover:bg-[var(--rt-surface-hover)] text-[var(--rt-text-muted)] hover:text-[var(--rt-danger)] transition-colors"
                    title="Delete"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="text-xs text-[var(--rt-text-muted)] mt-4 max-w-xl leading-relaxed">
        <strong>How this works:</strong> We prompt each AI to list authoritative websites for the
        keyword (no live web search), then parse the response for your domain. A high % means the
        AIs consider your site authoritative for that topic — which makes it more likely to be cited
        in AI search answers.
      </p>
    </div>
  );
}
